import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Paperclip } from "lucide-react";
import { emailInspection } from "@/lib/api";

// One-tap "send the report to the customer" from the WO's Inspections
// row. Pre-fills the site contact; the report goes out as a PDF
// attachment and the send is stamped on the inspection (which clears the
// PM's dashboard action item).
export function InspectionEmailDialog({ inspection, wo, onSent, onClose }) {
  const [to, setTo] = useState(wo?.contact_email || wo?.site_contact_email || "");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(
    `Inspection Report — ${inspection?.site_name || inspection?.client_name || ""}`.trim()
  );
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!to.trim()) { toast.error("Add a recipient email"); return; }
    setSending(true);
    try {
      const res = await emailInspection(inspection.id, {
        to: to.trim(),
        cc: cc.split(",").map(s => s.trim()).filter(Boolean),
        subject,
        note,
      });
      if (res.data?.status === "sent") {
        toast.success(`Report emailed to ${to.trim()}`);
        onSent?.();
        onClose();
      } else {
        toast.error(
          res.data?.status === "skipped"
            ? `Not sent — ${res.data?.reason || "email automation is off"}`
            : "Send failed"
        );
      }
    } catch {
      toast.error("Send failed");
    }
    setSending(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="inspection-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="w-4 h-4 text-blue-500" />Email inspection to customer
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500 flex items-center gap-1.5 bg-slate-50 rounded-md p-2">
            <Paperclip className="w-3.5 h-3.5 shrink-0" />
            <span>
              <strong className="text-slate-700">{inspection?.template_name}</strong> will be attached as a PDF
              {inspection?.conducted_date ? ` · inspected ${inspection.conducted_date}` : ""}
            </span>
          </div>
          <div>
            <Label className="text-xs">To *</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" data-testid="inspection-email-to" />
          </div>
          <div>
            <Label className="text-xs">CC</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Comma separated" data-testid="inspection-email-cc" />
          </div>
          <div>
            <Label className="text-xs">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="inspection-email-subject" />
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything you want the customer to read first…" data-testid="inspection-email-note" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="inspection-email-cancel">Cancel</Button>
            <Button size="sm" disabled={sending} onClick={send} data-testid="inspection-email-send">
              {sending ? "Sending…" : "Send report"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
